export const keys = {
  workspace: {
    bootstrap: (locationId: number) => ['workspace', 'bootstrap', locationId] as const,
    sidebar: (locationId: number) => ['workspace', 'sidebar', locationId] as const,
  },
  calendar: {
    day: (locationId: number, date: string, filters = '') =>
      ['calendar', 'day', locationId, date, filters] as const,
    next7: (locationId: number, start: string, filters = '') =>
      ['calendar', 'next7', locationId, start, filters] as const,
    month: (locationId: number, year: number, month: number, filters = '') =>
      ['calendar', 'month', locationId, year, month, filters] as const,
  },
  birdEye: {
    live: (locationId: number) => ['bird-eye', locationId] as const,
    finished: (locationId: number, date?: string) =>
      ['bird-eye', 'finished', locationId, date ?? 'today'] as const,
  },
  appointment: {
    details: (id: number) => ['appointment', 'details', id] as const,
    detail: (id: number) => ['appointment', 'detail', id] as const,
    peek: (id: number) => ['appointment', 'peek', id] as const,
    notes: (id: number) => ['appointment', 'notes', id] as const,
  },
  patient: {
    search: (q: string, sort?: string) => ['patient', 'search', q, sort] as const,
    profile: (id: number) => ['patient', 'profile', id] as const,
    outputs: (id: number) => ['patient', 'outputs', id] as const,
    comms: (id: number, filters?: Record<string, unknown>) =>
      ['patient', 'comms', id, filters] as const,
  },
  insurance: {
    plans: (patientId: number) => ['insurance', 'plans', patientId] as const,
    estimate: (patientId: number, cdtCodes: string[]) =>
      ['insurance', 'estimate', patientId, cdtCodes] as const,
    claims: (patientId: number) => ['insurance', 'claims', patientId] as const,
    preauths: (patientId: number) => ['insurance', 'preauths', patientId] as const,
  },
  tpi: {
    cheatsheet: (patientId: number) => ['tpi', 'cheatsheet', patientId] as const,
    substrate: (patientId: number) => ['tpi', 'substrate', patientId] as const,
    treatments: (patientId: number) => ['tpi', 'treatments', patientId] as const,
    link: (patientId: number) => ['tpi', 'link', patientId] as const,
    asyncJob: (jobId: string) => ['tpi', 'async-job', jobId] as const,
  },
  triage: {
    list: (filters?: Record<string, unknown>) => ['triage', 'list', filters] as const,
    huddle: ['triage', 'huddle'] as const,
    assignees: (locationId?: number) => ['triage', 'assignees', locationId] as const,
    item: (uuid: string) => ['triage', 'item', uuid] as const,
    guidance: (uuid: string) => ['triage', 'guidance', uuid] as const,
  },
  email: {
    accounts: ['email', 'accounts'] as const,
    mailAccounts: ['email', 'mail-accounts'] as const,
    autopilot: ['email', 'autopilot'] as const,
    messages: (filters?: Record<string, unknown>) => ['email', 'messages', filters] as const,
    message: (id: string) => ['email', 'message', id] as const,
  },
  sms: {
    inbox: ['sms', 'inbox'] as const,
    thread: (conversationId: string, topic?: string) =>
      ['sms', 'thread', conversationId, topic] as const,
  },
  forms: {
    templates: ['forms', 'templates'] as const,
    templatesSimple: ['forms', 'templates-simple'] as const,
    template: (formId: string) => ['forms', 'template', formId] as const,
    blockCatalog: ['forms', 'block-catalog'] as const,
    blocks: (formId: string) => ['forms', 'blocks', formId] as const,
    conditions: (formId: string) => ['forms', 'conditions', formId] as const,
    triggerRules: (formId: string) => ['forms', 'trigger-rules', formId] as const,
    triggerParser: (formId: string) => ['forms', 'trigger-parser', formId] as const,
    lookup: (source: string, q: string) => ['forms', 'lookup', source, q] as const,
    queue: ['forms', 'queue'] as const,
    assignments: (locationId: number, status?: string, q?: string) =>
      ['forms', 'assignments', locationId, status ?? '', q ?? ''] as const,
    packages: ['forms', 'packages'] as const,
    themes: ['forms', 'themes'] as const,
    themePresets: ['forms', 'theme-presets'] as const,
    submission: (id: string) => ['forms', 'submission', id] as const,
    submissionDocument: (id: string) => ['forms', 'submission', id, 'document'] as const,
    submissionFile: (id: string, fileKey: string) => ['forms', 'submission', id, 'file', fileKey] as const,
    patientLink: (formId: string, assignmentId: string) =>
      ['forms', 'patient-link', formId, assignmentId] as const,
    publicGate: (token: string) => ['forms', 'public', token, 'gate'] as const,
    publicSession: (token: string) => ['forms', 'public', token, 'session'] as const,
    patientFill: (token: string) => ['forms', 'patient-fill', token] as const,
    patientFormDetail: (formId: string, submissionId: string) =>
      ['forms', 'patient-form-detail', formId, submissionId] as const,
  },
  kpi: {
    providers: (locationId: number, date?: string) =>
      ['kpi', 'providers', locationId, date] as const,
    today: (locationId: number, date?: string) => ['kpi', 'today', locationId, date] as const,
    collections: ['kpi', 'collections'] as const,
  },
  settings: {
    general: (locationId: number) => ['settings', 'general', locationId] as const,
    operatories: (locationId: number) => ['settings', 'operatories', locationId] as const,
    providers: ['settings', 'providers'] as const,
    carriers: ['settings', 'carriers'] as const,
    reasons: (locationId: number) => ['settings', 'reasons', locationId] as const,
    playbook: (scope?: string) => ['settings', 'playbook', scope] as const,
    calInsights: (view?: string) => ['settings', 'cal-insights', view] as const,
    kpi: (view?: string) => ['settings', 'kpi', view] as const,
    oppWindows: ['settings', 'opp-windows'] as const,
    cellColor: ['settings', 'cell-color'] as const,
    flags: ['settings', 'flags'] as const,
    emailInteg: ['settings', 'email-integration'] as const,
    productionGoals: ['settings', 'production-goals'] as const,
  },
  patientForms: (patientId: number) => ['patient', 'forms', patientId] as const,
  formsStats: ['forms', 'stats'] as const,
  publicUrgency: (token: string) => ['public', 'urgency', token] as const,
  publicUrgencyPage: (token: string) => ['public', 'urgency', token, 'page'] as const,
} as const;
